import { ApiError, fetchWithAuth, getJson, readBody } from "./client";

// Search-env lifecycle calls — provision / list / inspect / extend /
// teardown — that back the ExportDetail SearchEnvPanel and (future)
// the standalone /search-envs page.
//
// Endpoints proxy through `/api/v1/operator/search-envs/*` on the
// portal, which forwards to the operator with X-User injected and
// owner-gates mutate operations.

export type SearchEnvStatus =
  | "provisioning"
  | "indexing"
  | "ready"
  | "deleting"
  | "deleted"
  | "failed";

// MUI Chip color per status. Shared by every page that renders an
// env status chip so the vocabulary stays consistent.
export const SEARCH_ENV_STATUS_COLOR: Record<
  SearchEnvStatus,
  "default" | "primary" | "success" | "error" | "warning"
> = {
  provisioning: "primary",
  indexing: "primary",
  ready: "success",
  deleting: "warning",
  deleted: "default",
  failed: "error",
};

// Envs that won't change state on their own — used by polling
// shouldStop and by UI gates like the "Extend by" buttons. ``failed``
// IS in this set because a failed env doesn't auto-transition; it
// sits there until the user reindexes or deletes it.
export const TERMINAL_SEARCH_ENV_STATUSES = new Set<SearchEnvStatus>([
  "deleted",
  "failed",
]);

// Envs whose AWS resources still exist (and therefore still cost
// money + still warrant user action). The complement is just
// ``deleted``. Distinct from TERMINAL_SEARCH_ENV_STATUSES because a
// ``failed`` env is "no more changes coming" (terminal for polling
// purposes) but ALSO "still has a live OpenSearch domain" (alive for
// visibility / cost / action purposes). The dataset detail page and
// the datasets listview's Search column both use this — a failed env
// should stay visible until the user explicitly tears it down.
export function isLiveEnv(env: { status: string }): boolean {
  return env.status !== "deleted";
}

export interface SearchEnvCostBreakdown {
  // Incurred so far. Compute + storage from provisioned_at to now (or
  // teardown / expiry, whichever came first). Egress is sunk once
  // indexing completes.
  compute_usd: number;
  storage_usd: number;
  egress_usd: number;
  // Projection at TTL expiry. Same components, but compute/storage
  // include the remaining hours up to expires_at; egress is the full
  // sunk cost (paid once, regardless of TTL).
  projected_compute_usd: number;
  projected_storage_usd: number;
  projected_egress_usd: number;
}

// Operator-rendered explanation of how the cost numbers were derived.
// The portal shows ``formula_lines`` verbatim in an expandable code
// block so the customer (or whoever's auditing the spend) can see
// what was multiplied to arrive at each line item, plus the constants
// + inputs that fed the math. Kept opaque on the frontend — the
// operator owns the cost model.
export interface SearchEnvCostExplanation {
  constants: {
    compute_usd_per_node_hour: number;
    storage_usd_per_gib_hour: number;
    egress_usd_per_gib: number;
    egress_inflation_ratio: number;
  };
  inputs: {
    instance_class: string | null;
    instance_count: number;
    storage_gib_per_node: number;
    data_size_gib: number;
    billed_hours_so_far: number;
    remaining_hours_until_expiry: number;
    indexing_complete: boolean;
    status: string;
  };
  formula_lines: string[];
}

// Live progress fields the operator hoists from the indexing Job row
// onto the env detail response. Only populated while ``status ==
// 'indexing'``; otherwise undefined.
export interface IndexingProgress {
  completed: number | null;
  total: number | null;
  message: string | null;
  updated_at: number | null;
  counters_json: string | null;
  // Worker pod identity, surfaced so the UI can show which paim
  // build is doing the indexing — useful when a stuck or slow run
  // needs to be correlated to a specific image tag. Operator
  // populates from the indexing Job row.
  k8s_job_name?: string | null;
  image?: string | null;
}

// Live AWS describe_domain result, hoisted onto the GET-one response.
// Lets the UI render "Domain: ACTIVE" / "Domain: gone" independently
// of the env's aggregated status field — important for the case where
// indexing failed but the domain is still up. ``error`` is set
// instead of the descriptor fields when describe_domain raised.
export interface DomainStatus {
  exists?: boolean;
  processing?: boolean;
  endpoint?: string | null;
  dashboards_url?: string | null;
  raw_status?: string | null;
  error?: string;
}

// Per-AZ health snapshot from DescribeDomainHealth. AWS returns the
// status as a string ("Active" / "Standby" / "Down" / …); we pass
// through unchanged.
export interface DomainHealthAz {
  zone: string;
  status: string;
}

// AWS control-plane health from DescribeDomainHealth — the same
// fields the AWS console renders as warnings ("Cluster health:
// yellow", "Master node unavailable", "Unassigned shards"). All
// scalar counts come back as strings because AWS sometimes emits
// "NotAvailable" in them; the SPA treats non-numeric as unknown.
// ``error`` is set instead of the descriptor fields when the
// upstream call raised.
export interface DomainHealth {
  exists?: boolean;
  cluster_health?: string | null;   // "Green" / "Yellow" / "Red"
  domain_state?: string | null;
  master_node?: string | null;      // "Available" / "Unavailable"
  total_shards?: string | null;
  unassigned_shards?: string | null;
  az_count?: string | null;
  active_az_count?: string | null;
  az_statuses?: DomainHealthAz[];
  error?: string;
}

// One row from the search_env_events table. Newest-first when returned
// by the list endpoint. ``kind`` is dotted-namespaced ("domain.active",
// "index.failed", "teardown.requested") and the portal maps it to an
// icon + colour in the activity timeline.
export interface SearchEnvEvent {
  id: number;
  env_id: string;
  ts: number;
  level: "info" | "warning" | "error";
  kind: string;
  message: string | null;
  fields_json: string | null;
}

export interface SearchEnv {
  id: string;
  workflow_id: string;
  status: SearchEnvStatus;
  domain_name: string | null;
  endpoint_url: string | null;
  dashboards_url: string | null;
  instance_class: string | null;
  instance_count: number | null;
  storage_gib: number | null;
  data_size_gib: number | null;
  doc_count: number | null;
  // v8: user-supplied or auto-derived deadline (hours) for the
  // indexing job. Editable via PATCH /indexing-deadline while status
  // == 'indexing'. Null on pre-v8 envs.
  indexing_timeout_hours?: number | null;
  master_user: string | null;
  // Present iff the viewer is the env's submitter (or it's a CLI-
  // submitted env, which the operator treats as world-readable inside
  // its trust boundary). Use this to gate the credentials-reveal UI.
  master_password?: string | null;
  // Limited-permission customer-facing account — what we hand to the
  // end user. Always populated for envs provisioned after the v7
  // schema bump; NULL for pre-v7 envs (a re-provision after upgrade
  // gets them).
  customer_user: string | null;
  customer_password?: string | null;
  submitted_by: string | null;
  submitted_at: number;          // ms epoch
  provisioned_at: number | null; // domain ACTIVE
  indexed_at: number | null;     // bulk push complete
  expires_at: number;
  deleted_at: number | null;
  error_message: string | null;
  job_id: string | null;
  // v11: customer-facing URL for the per-env MCP service. Populated
  // by the reconciler after the env reaches ``ready`` (concurrent
  // with the OpenSearch domain going live). Null when MCP wasn't
  // provisioned (older envs, or the operator wasn't started with
  // PAIM_CSE_CERT_ARN). The handoff doc + SearchEnvCard link to it
  // when present.
  mcp_endpoint_url: string | null;
  // Derived fields the operator computes on each GET — not stored in
  // the row. Always present on the detail response; the list response
  // omits them to keep the table query fast.
  incurred_usd?: number;
  projected_usd?: number;
  breakdown?: SearchEnvCostBreakdown;
  cost_explanation?: SearchEnvCostExplanation;
  indexing_progress?: IndexingProgress;
  // v8: live describe_domain result; absent on the list endpoint.
  domain_status?: DomainStatus;
  // Live DescribeDomainHealth — the AWS-console "warnings" source.
  // Same lifecycle as domain_status; absent on list endpoint.
  domain_health?: DomainHealth;
}

export interface ListSearchEnvsResponse {
  search_envs: SearchEnv[];
  limit: number;
  offset: number;
}

export interface SizingPreview {
  workflow_id: string;
  data_size_gib: number;
  ttl_hours: number;
  // ``null`` when data exceeds the auto-sizing ceiling AND the caller
  // didn't pass an ``instance_class`` override. UI flips into override
  // mode when this happens. Populated otherwise — either with the
  // auto-pick or with the caller's override.
  recommended_sizing: {
    instance_class: string;
    instance_count: number;
    storage_gib: number;
    hourly_usd: number;
  } | null;
  // ``compute_usd`` / ``storage_usd`` are null in the same "above
  // ceiling + no override" case. ``egress_usd`` is always present
  // (data-size-only, computable regardless of sizing).
  cost_breakdown: {
    compute_usd: number | null;
    storage_usd: number | null;
    egress_usd: number;
  };
  estimated_cost_usd: number | null;
  // Operator-rendered formula breakdown. Same shape as the
  // ``cost_explanation`` field on a live env — the provision modal
  // renders ``formula_lines`` verbatim in an expandable code block so
  // the customer can see exactly what was multiplied to arrive at the
  // pre-provision estimate. Optional so pre-rc64 operators that
  // don't return it still parse cleanly (UI just hides the chevron).
  cost_explanation?: SearchEnvCostExplanation;
  // Human-readable explanation when ``recommended_sizing`` is null.
  // The UI surfaces this verbatim in the override prompt.
  sizing_error: string | null;
  // v8: auto-scaled deadline (hours) for the indexing job, computed
  // from data_size + instance_count via opensearch_admin.suggest_timeout_hours.
  // The modal pre-fills its Timeout input from this value.
  suggested_timeout_hours: number;
  // v9: auto-scaled number_of_shards for the paim-{env_id} index.
  // Modal pre-fills its Shards input from this; user can override.
  suggested_shards: number;
}

export interface ProvisionSearchEnvParams {
  workflow_id: string;
  ttl_hours: number;
  instance_class?: string;
  instance_count?: number;
  storage_gib?: number;
  // v8: indexing-job deadline in hours. If omitted the operator
  // auto-scales from data_size + instance_count. Clamped 1..48.
  timeout_hours?: number;
  // v9: number_of_shards for the paim-{env_id} index. If omitted the
  // operator auto-scales from data_size + instance_count. Clamped 1..24.
  shards?: number;
}

export interface ProvisionSearchEnvResponse {
  env_id: string;
  status: string;
  data_size_gib: number;
  instance_class: string;
  instance_count: number;
  estimated_cost_usd: number;
}

export interface PatchSearchEnvResponse extends SearchEnv {
  granted_hours: number;
  requested_hours: number;
  // When non-null, the source workflow's output_expires_at was bumped
  // forward to match the env's new expires_at (cascading TTL so the
  // dataset survives at least as long as the env does). The UI uses
  // this to render a "dataset retention also extended" notice.
  dataset_extended_to: number | null;
}

// Customer-handoff payload — the operator returns this; the frontend
// renders it into email-ready plain text + a downloadable PDF.
// Schema mirrors src/paim/operator/handoff.py:build_handoff.
export interface HandoffSchemaField {
  name: string;
  type: string;
  description: string;
}

export interface HandoffSampleQuery {
  title: string;
  curl: string;
  description: string;
}

export interface HandoffCompatibleClient {
  name: string;
  language: string;
  url: string;
  note?: string;
}

export interface SearchEnvHandoff {
  env_id: string;
  domain_name: string;
  // Operator still includes ``instance_class`` in the payload for
  // debug visibility, but the handoff PDF / text rendering MUST NOT
  // surface it to the customer. Treat as internal.
  instance_class: string | null;
  data_size_gib: number | null;
  doc_count: number | null;
  // ms epoch. ``submitted_at`` is when the user clicked Provision;
  // ``provisioned_at`` is when AWS reported the domain ACTIVE;
  // ``indexed_at`` is when bulk load finished. Each can be null on
  // pre-v8 envs or while the env is still being set up.
  submitted_at: number | null;
  provisioned_at: number | null;
  indexed_at: number | null;
  expires_at: number;
  workflow_id: string;
  dashboards: {
    url: string;
    default_dashboard_title: string;
    first_login_note: string;
  };
  api: {
    endpoint_url: string;
    index_name: string;
    auth: string;
  };
  // Per-env MCP server access details. Null on the handoff when the
  // env wasn't provisioned with MCP (PAIM_CSE_CERT_ARN unset, or the
  // env predates rc80). Carries the URL plus a short note for the
  // customer to paste into their MCP client config.
  mcp?: {
    endpoint_url: string;
    transport: "streamable-http";
    auth: string;
    note: string;
  } | null;
  customer_credentials: {
    user: string;
    password: string;
  };
  schema: HandoffSchemaField[];
  sample_queries: HandoffSampleQuery[];
  compatible_clients: HandoffCompatibleClient[];
  support: {
    contact: string;
  };
  // Source-export filter expressions. ``null`` when the operator
  // couldn't resolve the source workflow (e.g. orphan env). Both
  // strings are present together when populated; empty string means
  // the user submitted the export with no filter of that kind.
  dataset_configuration: {
    input_filter: string;
    lucene_filter: string;
  } | null;
}

export function getHandoff(envId: string): Promise<SearchEnvHandoff> {
  return getJson<SearchEnvHandoff>(
    `/api/v1/operator/search-envs/${envId}/handoff`,
  );
}

export function listSearchEnvs(
  opts: {
    status?: SearchEnvStatus;
    workflow_id?: string;
    limit?: number;
    offset?: number;
  } = {},
): Promise<ListSearchEnvsResponse> {
  const q = new URLSearchParams();
  if (opts.status) q.set("status", opts.status);
  if (opts.workflow_id) q.set("workflow_id", opts.workflow_id);
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.offset !== undefined) q.set("offset", String(opts.offset));
  const qs = q.toString() ? `?${q.toString()}` : "";
  return getJson<ListSearchEnvsResponse>(`/api/v1/operator/search-envs${qs}`);
}

export function getSearchEnv(envId: string): Promise<SearchEnv> {
  return getJson<SearchEnv>(`/api/v1/operator/search-envs/${envId}`);
}

export function getSizingPreview(
  workflowId: string,
  ttlHours: number,
  override?: { instance_class: string; instance_count?: number; storage_gib?: number },
): Promise<SizingPreview> {
  const q = new URLSearchParams({
    workflow_id: workflowId,
    ttl_hours: String(ttlHours),
  });
  if (override) {
    q.set("instance_class", override.instance_class);
    if (override.instance_count !== undefined)
      q.set("instance_count", String(override.instance_count));
    if (override.storage_gib !== undefined)
      q.set("storage_gib", String(override.storage_gib));
  }
  return getJson<SizingPreview>(
    `/api/v1/operator/search-envs/sizing-preview?${q.toString()}`,
  );
}

export interface ListSearchEnvEventsResponse {
  env_id: string;
  events: SearchEnvEvent[];
}

export function listSearchEnvEvents(
  envId: string,
  opts: { limit?: number } = {},
): Promise<ListSearchEnvEventsResponse> {
  const q = new URLSearchParams();
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  const qs = q.toString() ? `?${q.toString()}` : "";
  return getJson<ListSearchEnvEventsResponse>(
    `/api/v1/operator/search-envs/${envId}/events${qs}`,
  );
}

export async function patchIndexingDeadline(
  envId: string,
  hours: number,
): Promise<{ env_id: string; indexing_timeout_hours: number }> {
  const res = await fetchWithAuth(
    `/api/v1/operator/search-envs/${envId}/indexing-deadline`,
    {
      method: "PATCH",
      body: JSON.stringify({ hours }),
    },
  );
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `patch indexing-deadline returned ${res.status}`,
    );
  }
  return (await res.json()) as { env_id: string; indexing_timeout_hours: number };
}

export async function provisionSearchEnv(
  params: ProvisionSearchEnvParams,
): Promise<ProvisionSearchEnvResponse> {
  const res = await fetchWithAuth("/api/v1/operator/search-envs", {
    method: "POST",
    body: JSON.stringify(params),
  });
  if (res.status !== 202) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `provision returned ${res.status}`,
    );
  }
  return (await res.json()) as ProvisionSearchEnvResponse;
}

export async function patchSearchEnv(
  envId: string,
  patch: { ttl_hours: number },
): Promise<PatchSearchEnvResponse> {
  const res = await fetchWithAuth(`/api/v1/operator/search-envs/${envId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    throw new ApiError(res.status, await readBody(res), `patch returned ${res.status}`);
  }
  return (await res.json()) as PatchSearchEnvResponse;
}

export async function deleteSearchEnv(envId: string): Promise<void> {
  const res = await fetchWithAuth(`/api/v1/operator/search-envs/${envId}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) {
    throw new ApiError(res.status, await readBody(res), `delete returned ${res.status}`);
  }
}

// Kill the in-flight indexing Job without tearing down the domain.
// Use when the indexer is stuck or running too slow but you want to
// keep the credentials and pay nothing to re-provision. Env
// transitions to `failed` with an error_message; trigger reindex to
// retry, or delete to tear down.
export async function cancelIndexing(envId: string): Promise<void> {
  const res = await fetchWithAuth(
    `/api/v1/operator/search-envs/${envId}/cancel-indexing`,
    { method: "POST" },
  );
  if (!res.ok && res.status !== 204) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `cancel-indexing returned ${res.status}`,
    );
  }
}

// Drop the OpenSearch index + re-run the bulk indexer against the
// same domain. Credentials / domain / security plugin bindings
// persist; only the index data is recreated. Returns the new job_id.
export async function reindexSearchEnv(
  envId: string,
): Promise<{ env_id: string; status: string; job_id: string }> {
  const res = await fetchWithAuth(
    `/api/v1/operator/search-envs/${envId}/reindex`,
    { method: "POST" },
  );
  if (!res.ok && res.status !== 202) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `reindex returned ${res.status}`,
    );
  }
  return (await res.json()) as {
    env_id: string;
    status: string;
    job_id: string;
  };
}

// Helpers used by the UI.

export function ttlRemainingMs(env: SearchEnv): number {
  return Math.max(0, env.expires_at - Date.now());
}

export function formatTtl(ms: number): string {
  if (ms <= 0) return "expired";
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

export function ttlChipSeverity(
  ms: number,
): "ok" | "amber" | "red" {
  if (ms < 5 * 60 * 1000) return "red";
  if (ms < 30 * 60 * 1000) return "amber";
  return "ok";
}
